/**
 * O que faz: Página para definir nova senha após clicar no link de recuperação
 * Onde usa: Rota /redefinir-senha acessível via link enviado por email
 * Conexões: AuthContext (updatePassword), Supabase Auth
 * Tabelas: N/A (apenas auth do Supabase)
 * Autor: IA
 * Data: 04/02/2026
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Página de Redefinição de Senha
 * 
 * Permite que o usuário defina uma nova senha após clicar no link
 * de recuperação enviado por email.
 * 
 * Fluxo:
 * 1. Usuário clica no link do email (contém token de recuperação)
 * 2. Supabase valida o token e estabelece uma sessão temporária
 * 3. Usuário digita nova senha
 * 4. Sistema atualiza a senha e redireciona para login
 */
export const ResetPasswordPage = () => {
    const { updatePassword } = useAuth();
    const navigate = useNavigate();

    // Estados do formulário
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Estados de verificação do token
    const [tokenValid, setTokenValid] = useState<boolean | null>(null);
    const [checkingToken, setCheckingToken] = useState(true);
    const [passwordUpdated, setPasswordUpdated] = useState(false);

    // Verificar se existe uma sessão de recuperação válida ao carregar a página
    // O Supabase automaticamente processa o token da URL e estabelece uma sessão
    useEffect(() => {
        const checkRecoverySession = async () => {
            try {
                // O Supabase Auth processa automaticamente os parâmetros da URL
                // e estabelece uma sessão se o token for válido
                const { data: { session }, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('[ResetPasswordPage] Erro ao verificar sessão:', error);
                    setTokenValid(false);
                    return;
                }

                // Verifica se existe uma sessão (indica que o token foi processado)
                if (session) {
                    console.log('[ResetPasswordPage] Sessão de recuperação válida');
                    setTokenValid(true);
                } else {
                    // Pode ser que o token ainda esteja sendo processado na URL
                    // Aguarda um pouco e verifica novamente
                    const hashParams = new URLSearchParams(window.location.hash.substring(1));
                    const accessToken = hashParams.get('access_token');
                    const type = hashParams.get('type');

                    if (accessToken && type === 'recovery') {
                        // Token presente na URL, aguarda processamento
                        console.log('[ResetPasswordPage] Token de recuperação detectado na URL');
                        setTokenValid(true);
                    } else {
                        console.log('[ResetPasswordPage] Nenhum token de recuperação encontrado');
                        setTokenValid(false);
                    }
                }
            } catch (error) {
                console.error('[ResetPasswordPage] Erro ao verificar token:', error);
                setTokenValid(false);
            } finally {
                setCheckingToken(false);
            }
        };

        // Listener para eventos de autenticação (captura quando o token é processado)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                console.log('[ResetPasswordPage] Evento PASSWORD_RECOVERY recebido');
                setTokenValid(true);
                setCheckingToken(false);
            }
        });

        checkRecoverySession();

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    // Validar formulário
    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Validação da senha: mínimo 6 caracteres (padrão Supabase)
        if (!password) {
            newErrors.password = 'Nova senha é obrigatória';
        } else if (password.length < 6) {
            newErrors.password = 'A senha deve ter pelo menos 6 caracteres';
        }

        // Validação de confirmação
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirme sua nova senha';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'As senhas não coincidem';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submeter formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Verifique os campos do formulário');
            return;
        }

        try {
            setLoading(true);
            await updatePassword(password);
            setPasswordUpdated(true);

            // Redirecionar para login após 3 segundos
            setTimeout(() => {
                navigate('/login', { replace: true });
            }, 3000);
        } catch (err: any) {
            console.error('Erro ao atualizar senha:', err);
        } finally {
            setLoading(false);
        }
    };

    // Limpar erro do campo quando usuário começa a digitar
    const handleInputChange = (field: string, value: string) => {
        if (field === 'password') {
            setPassword(value);
        } else {
            setConfirmPassword(value);
        }

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Loading em tela cheia enquanto valida o token do e-mail de recuperação
    if (checkingToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
                <Card className="w-full max-w-md rounded-2xl shadow-lg border-green-200">
                    <CardContent className="flex flex-col items-center justify-center p-8">
                        <ThinkingOrbLoader state="solving" size={64} label="Verificando link de recuperação..." />
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Token inválido ou expirado
    if (tokenValid === false) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 px-4">
                <Card className="w-full max-w-md rounded-2xl shadow-lg border-red-200">
                    <CardContent className="pt-8 pb-6 text-center">
                        <div className="flex items-center justify-center w-16 h-16 bg-red-100 mx-auto mb-6 rounded-full">
                            <XCircle className="w-10 h-10 text-red-600" />
                        </div>

                        <CardTitle className="text-2xl font-bold text-gray-900 mb-3">
                            Link inválido ou expirado
                        </CardTitle>

                        <CardDescription className="text-base mb-6">
                            O link de recuperação de senha é inválido ou já expirou.
                            <br /><br />
                            Solicite um novo link para redefinir sua senha.
                        </CardDescription>

                        <Link to="/esqueci-senha">
                            <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold shadow-md hover:shadow-lg transition-all duration-200">
                                Solicitar novo link
                            </Button>
                        </Link>

                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors mt-4"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao login
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Senha atualizada com sucesso
    if (passwordUpdated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 px-4">
                <Card className="w-full max-w-md rounded-2xl shadow-lg border-green-200">
                    <CardContent className="pt-8 pb-6 text-center">
                        <div className="flex items-center justify-center w-16 h-16 bg-green-100 mx-auto mb-6 rounded-full">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>

                        <CardTitle className="text-2xl font-bold text-gray-900 mb-3">
                            Senha atualizada!
                        </CardTitle>

                        <CardDescription className="text-base mb-6">
                            Sua senha foi redefinida com sucesso.
                            <br /><br />
                            Você será redirecionado para o login em instantes...
                        </CardDescription>

                        <Link to="/login">
                            <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold shadow-md hover:shadow-lg transition-all duration-200">
                                Ir para o login
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Formulário de nova senha
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 px-4">
            <Card className="w-full max-w-md rounded-2xl shadow-lg border-green-200">
                <CardHeader className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 mx-auto mb-4 shadow-md rounded-full">
                        <Lock className="w-8 h-8 text-white" />
                    </div>

                    <CardTitle className="text-2xl font-bold text-gray-900">
                        Criar nova senha
                    </CardTitle>

                    <CardDescription>
                        Digite sua nova senha abaixo. Ela deve ter pelo menos 6 caracteres.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Campo Nova Senha */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Nova senha
                            </label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    placeholder="Digite sua nova senha"
                                    className={`rounded-xl transition-all duration-200 pr-10 ${errors.password
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-green-200 focus:border-green-400 focus:ring-green-100'
                                        }`}
                                    autoComplete="new-password"
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-green-500 hover:text-green-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password}</p>}
                        </div>

                        {/* Campo Confirmar Senha */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                Confirmar nova senha
                            </label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    placeholder="Confirme sua nova senha"
                                    className={`rounded-xl transition-all duration-200 pr-10 ${errors.confirmPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-green-200 focus:border-green-400 focus:ring-green-100'
                                        }`}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-green-500 hover:text-green-600 transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.confirmPassword && <p className="text-sm text-red-600 mt-1">{errors.confirmPassword}</p>}
                        </div>

                        {/* Botão de Salvar */}
                        <Button
                            type="submit"
                            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar nova senha'
                            )}
                        </Button>
                    </form>

                    {/* Link para voltar ao login */}
                    <div className="mt-6 text-center">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors rounded-lg px-3 py-2 hover:bg-green-50"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao login
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResetPasswordPage;
